//導向綠界付款頁
//
//  綠界只接受「真的表單 POST」—— 用 axios 送出去沒有用，
//  因為我們需要的是「瀏覽器整頁跳轉過去」，而不是拿回一份 HTML。
//  所以動態建一個隱藏表單再 submit()。
//
//  也不能改成 GET 把參數塞網址：綠界只收 POST，而且參數裡有 CheckMacValue
//  與金額，放進網址會留在瀏覽紀錄與 referer 裡。
//
//  參數（含 CheckMacValue）一律由後端產生，前端只負責搬運，
//  在這裡改任何值都會讓檢查碼對不上、被綠界退件。

//開給綠界的那個分頁的名字。先開空白分頁、再讓表單 target 指到同一個名字，
//  兩邊就會接上（見下面兩支函式）。
const ECPAY_WINDOW = "ecpay-checkout";

//「這個分頁是開給綠界結帳用的」。
//
//  記號寫在**新開的那個分頁自己的 sessionStorage** 裡，不是原分頁的。
//  sessionStorage 是「每個分頁 × 每個來源」各自獨立的，而且**跟著分頁活著** ——
//  那個分頁導去綠界（不同來源）再導回我們這邊時，這個值還在（實測過）。
//
//  —— 為什麼不問原分頁「你在嗎」——
//  第一版是用 BroadcastChannel 廣播、等原分頁回應，收到才關掉自己。
//  桌機沒問題，但 **iOS Safari 會凍結背景分頁** —— 使用者人在綠界那一頁時，
//  原本的閻異閣根本沒在執行 JS，回不了那一聲。於是手機上永遠等不到回應，
//  最後落在付款結果頁，變成一個多出來的中轉頁。
//
//  改成自己認自己就沒有這個問題：不依賴任何別的分頁醒著，也沒有等待時間。
export const POPUP_KEY = "ecpay-is-checkout-tab";

//—— 先把分頁開起來 ——
//
//  要讓綠界開在新分頁而不是蓋掉目前這一頁，光是給表單 target="_blank" 不夠：
//  建單是一支非同步 API，等它回來時已經離開使用者按下按鈕的那個事件，
//  瀏覽器會把這時候才開的新視窗判定成「不是使用者要求的」而擋掉。
//
//  所以拆成兩步：按下去的當下先用這支把一個「空的具名分頁」開起來
//  （那一刻還在使用者手勢裡，不會被擋），建單回來之後再把表單
//  target 指向那個名字，內容就送進已經開好的分頁。
//
//  回傳 null 代表還是被擋了（使用者自己關掉彈窗權限）——
//  呼叫端要退回原本的整頁跳轉，不能讓人按了沒反應。
//—— 等待中的那一頁 ——
//
//  這一頁活在建單 API 來回的那段時間（本機幾毫秒，Cloud Run 冷啟動時
//  可能一兩秒），之後就被綠界的頁面整個換掉。
//
//  為什麼不留空白：不寫東西的話那個分頁是 about:blank，多數瀏覽器是**白的**，
//  在全黑的 app 上會閃一下白，比現在這個深色過場更明顯。
//
//  —— 為什麼全部寫死在字串裡 ——
//  這是另一個 document，拿不到 app 的 CSS，連字型也要自己宣告。
//  也刻意不載圖：那一頁隨時會被綠界換掉，圖來不及下載就是空的，
//  真的 404 反而看起來像壞了。轉圈用 CSS 畫，沒有額外請求。
//
//  色票跟 styles/_variables 對齊：
//    背景 #000000（$c-bg）／ 金 #d9b26a（$c-gold）／ 次要文字 #9494a8（$c-muted）
const HOLDING_PAGE =
  '<!doctype html><html lang="zh-Hant"><head><meta charset="utf-8">' +
  '<meta name="viewport" content="width=device-width,initial-scale=1">' +
  "<title>前往綠界</title><style>" +
  "*{box-sizing:border-box}" +
  "body{margin:0;height:100vh;display:flex;flex-direction:column;" +
  "align-items:center;justify-content:center;gap:22px;background:#000000;" +
  "color:#f2f2f6;font-family:'Noto Serif TC','Songti TC','PMingLiU'," +
  "'MingLiU',-apple-system,BlinkMacSystemFont,serif}" +
  //  站名。字距拉開 —— 三個字居中時太擠會像一團
  ".b{font-size:20px;font-weight:700;letter-spacing:.42em;" +
  //  letter-spacing 會在最後一個字右邊也補一份，往左推回去才是真的置中
  "text-indent:.42em;color:#d9b26a}" +
  //  轉圈。只有一段是金的，其餘透明 —— 轉起來才看得出在動
  ".s{width:26px;height:26px;border:2px solid rgba(217,178,106,.22);" +
  "border-top-color:#d9b26a;border-radius:50%;animation:r .8s linear infinite}" +
  ".t{font-size:13px;letter-spacing:.08em;color:#9494a8}" +
  "@keyframes r{to{transform:rotate(360deg)}}" +
  //  會動的東西一律尊重系統的「減少動態效果」
  "@media(prefers-reduced-motion:reduce){.s{animation:none}}" +
  "</style></head><body>" +
  '<div class="b">閻異閣</div>' +
  '<div class="s"></div>' +
  '<div class="t">正在前往綠界付款</div>' +
  "</body></html>";

export const openEcpayWindow = () => {
  const win = window.open("", ECPAY_WINDOW);
  if (!win) return null;
  //先畫一頁，不然使用者會先看到一個空白分頁、以為壞了
  try {
    win.document.write(HOLDING_PAGE);
    //  write 之後要 close，不然分頁會一直停在「載入中」的狀態
    //  （轉圈圖示跟我們自己畫的那顆會疊在一起）
    win.document.close();
  } catch {
    //跨來源之類的狀況寫不進去，不影響後面的表單送出
  }
  //  讓那個分頁記得自己是結帳開出來的。
  //  綠界付完款導回 ecpay-done.html 時，它靠這個值決定要關掉自己
  //  還是自己導去付款結果頁（彈窗被擋時只有一個分頁，關了就什麼都沒有）。
  try {
    win.sessionStorage.setItem(POPUP_KEY, "1");
  } catch (e) {
    //  無痕模式或封鎖儲存時寫不進去。那種情況 ecpay-done 會當作
    //  「不是結帳分頁」而自己導去付款結果頁 —— 退回舊行為，不會壞。
  }

  return win;
};

//  win 傳入 openEcpayWindow() 開好的分頁就送進那一頁；
//  傳 null（被擋掉）就照舊整頁跳轉。
export const submitToEcpay = (action, params, win) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  //accept-charset：綠界要求 UTF-8，中文品名才不會變亂碼
  form.acceptCharset = "UTF-8";
  if (win) form.target = ECPAY_WINDOW;
  Object.entries(params).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    //一律轉字串：數字型別在某些瀏覽器會被格式化（1680 變 "1,680"），
    //  而 CheckMacValue 是拿原始字串算的，差一個逗號就驗不過。
    input.value = String(value);
    form.appendChild(input);
  });

  //必須先掛進 DOM 才能 submit
  document.body.appendChild(form);
  form.submit();
  //送進新分頁的話這一頁還在，表單留著只是垃圾
  if (win) form.remove();
};
